import { Link } from "react-router-dom";
import { ConnectionBrandIcon } from "@/components/ConnectionBrandIcon";
import { PublicDocLayout, PublicSectionAnchor } from "@/components/PublicDocLayout";
import { PublicLayout } from "@/components/PublicLayout";
import { PublicPageHero } from "@/components/PublicPageHero";
import { SUPPORT_EMAIL, SUPPORT_HOURS, SUPPORT_RESPONSE } from "@/lib/publicSite";

const GUIDE_TOC = [
  { id: "prerequisites", label: "Prerequisites" },
  { id: "add", label: "Adding the app" },
  { id: "use", label: "Using Hub with Zoom" },
  { id: "remove", label: "Removing the app" },
  { id: "data", label: "Your data after removal" },
  { id: "troubleshooting", label: "Troubleshooting" },
  { id: "support", label: "Support" },
] as const;

export function DocsZoomPage() {
  return (
    <PublicLayout title="ATW Hub for Zoom — Integration guide">
      <article className="public-doc">
        <PublicPageHero
          eyebrow="Guide"
          title="ATW Hub for Zoom"
          lede="How to add, use, and remove ATW Hub's Zoom Team Chat and Meetings integration."
          updated="October 1, 2026"
          marks={<ConnectionBrandIcon id="zoom" size={40} />}
        />

        <PublicDocLayout toc={[...GUIDE_TOC]}>
          <section id="prerequisites" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="prerequisites" />
              Prerequisites
            </h2>
            <ul className="public-list">
              <li>An ATW account with Hub enabled.</li>
              <li>A Zoom account with Team Chat and Meetings available on your plan.</li>
              <li>Permission to authorize OAuth apps for your Zoom account (or your Zoom admin's help).</li>
            </ul>
          </section>

          <section id="add" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="add" />
              Adding the app
            </h2>
            <ol className="public-steps">
              <li>Sign in to Hub at hub.atw.net.</li>
              <li>
                Open <strong>Settings</strong> → <strong>Connections</strong> → <strong>Chat</strong> or{" "}
                <strong>Meetings</strong>, depending on what you want to connect.
              </li>
              <li>
                Click <strong>Connect Zoom</strong> (Team Chat) or connect Zoom Meetings from the Meetings
                tab. Hub sends you to Zoom's authorization page for the ATW Hub OAuth app.
              </li>
              <li>Review the requested permissions and click <strong>Allow</strong>.</li>
              <li>Return to Hub. The connection should show as connected.</li>
            </ol>
            <p>
              If you already connected Zoom Meetings before Team Chat scopes were added, connect again so
              Zoom can grant the Team Chat permissions Hub needs.
            </p>
            <p>
              Unpublished Zoom apps work only for users in the developer's Zoom account until Zoom approves
              beta or Marketplace distribution. Contact {SUPPORT_EMAIL} if you need access during pilot
              testing.
            </p>
          </section>

          <section id="use" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="use" />
              Using Hub with Zoom
            </h2>
            <h3>Team Chat in Hub</h3>
            <p>
              Open <strong>Chat</strong> in Hub and select your Zoom account. Hub lists your Zoom Team Chat
              channels and direct messages. Open a conversation to load messages live from Zoom (Hub does not
              store message text). Send, edit, and delete your own messages from Hub when Zoom allows it.
            </p>
            <h3>Meetings in Hub</h3>
            <p>
              Open <strong>Meetings</strong> to see upcoming Zoom meetings from your connected account and
              join or schedule according to your Hub settings.
            </p>
            <h3>What Hub does not do</h3>
            <ul className="public-list">
              <li>Hub does not scan Zoom chat in the background.</li>
              <li>Hub does not bypass Zoom retention or admin policies.</li>
              <li>Hub does not share your Zoom access with other Hub users.</li>
            </ul>
          </section>

          <section id="remove" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="remove" />
              Removing the app
            </h2>
            <h3>Disconnect in Hub</h3>
            <p>
              Settings → Connections → Chat or Meetings → <strong>Disconnect</strong> next to Zoom. Hub
              deletes stored tokens and read markers for that connection.
            </p>
            <h3>Remove from your Zoom account</h3>
            <ol className="public-steps">
              <li>Sign in to the <a href="https://marketplace.zoom.us/">Zoom App Marketplace</a>.</li>
              <li>Click <strong>Manage</strong> → <strong>Added Apps</strong> (or search for ATW Hub).</li>
              <li>Select <strong>ATW Hub</strong> and click <strong>Remove</strong>.</li>
            </ol>
            <p>
              For published apps, Zoom also sends an <code>app_deauthorized</code> notification to Hub.
              Hub verifies the request and deletes your Zoom chat connection data and matching Meetings
              tokens.
            </p>
          </section>

          <section id="data" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="data" />
              Your data after removal
            </h2>
            <p>
              After disconnect or deauthorization, Hub deletes OAuth tokens, read markers, and in-memory
              caches for your Zoom user. Hub does not keep Zoom message text. Messages you sent from Hub
              remain in Zoom. See the <Link to="/privacy">Privacy Policy</Link>.
            </p>
          </section>

          <section id="troubleshooting" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="troubleshooting" />
              Troubleshooting
            </h2>
            <ul className="public-list">
              <li>
                <strong>Rate limits:</strong> if Zoom returns HTTP 429, wait for the time shown and try
                again.
              </li>
              <li>
                <strong>Missing chats:</strong> confirm Team Chat is enabled for your Zoom account and that
                you re-authorized after chat scopes were added.
              </li>
              <li>
                <strong>Meetings only, no chat:</strong> connect Zoom from the Chat connections tab, not
                only Meetings.
              </li>
            </ul>
          </section>

          <section id="support" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="support" />
              Support
            </h2>
            <p>
              Email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. {SUPPORT_RESPONSE}{" "}
              {SUPPORT_HOURS}. <Link to="/support">hub.atw.net/support</Link>.
            </p>
          </section>
        </PublicDocLayout>
      </article>
    </PublicLayout>
  );
}
